import { useMemo } from 'react';
import { Box, Button, Sheet, Stack, Typography } from '@mui/joy';

const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const PRESETS = {
    always: { label: 'Круглосуточно', build: () => ({}) },
    work: {
        label: 'Будни 9–19',
        build: () => {
            const hours = HOURS.filter((h) => h >= 9 && h < 19);
            return { 0: hours, 1: hours, 2: hours, 3: hours, 4: hours };
        },
    },
    evening: {
        label: 'Вечера и выходные',
        build: () => {
            const evening = HOURS.filter((h) => h >= 18 && h < 24);
            const weekend = HOURS.filter((h) => h >= 10 && h < 24);
            return { 0: evening, 1: evening, 2: evening, 3: evening, 4: evening,
                     5: weekend, 6: weekend };
        },
    },
};


/**
 * Временной таргетинг: сетка 7×24.
 *
 * Формат совпадает с серверным: { "0": [9,10,11], ... }, 0 = понедельник.
 * Пустой объект означает круглосуточно — так же трактует сервер,
 * поэтому «выключить всё» и «показывать всегда» это одно и то же состояние.
 */
export default function AdScheduleEditor({ value, onChange, disabled }) {
    const schedule = value || {};

    const isOn = (day, hour) => {
        if (!Object.keys(schedule).length) return true; // пусто = круглосуточно
        const hours = schedule[String(day)] || schedule[day] || [];
        return hours.includes(hour);
    };

    const isAlways = useMemo(() => !Object.keys(schedule).length, [schedule]);

    const toggle = (day, hour) => {
        if (disabled) return;
        // Первый клик по «круглосуточному» расписанию разворачивает его
        // в явную сетку, иначе снять один час было бы невозможно.
        const base = isAlways
            ? Object.fromEntries(DAYS.map((_, d) => [String(d), [...HOURS]]))
            : { ...schedule };

        const key = String(day);
        const hours = new Set(base[key] || []);
        if (hours.has(hour)) hours.delete(hour);
        else hours.add(hour);

        const next = { ...base, [key]: [...hours].sort((a, b) => a - b) };
        // Все дни забиты полностью → возвращаемся к пустому объекту
        const full = DAYS.every((_, d) => (next[String(d)] || []).length === 24);
        onChange(full ? {} : next);
    };

    const toggleDay = (day) => {
        if (disabled) return;
        const base = isAlways
            ? Object.fromEntries(DAYS.map((_, d) => [String(d), [...HOURS]]))
            : { ...schedule };
        const key = String(day);
        const current = base[key] || [];
        const next = { ...base, [key]: current.length === 24 ? [] : [...HOURS] };
        const full = DAYS.every((_, d) => (next[String(d)] || []).length === 24);
        onChange(full ? {} : next);
    };

    return (
        <Box>
            <Stack direction="row" spacing={0.8} sx={{ mb: 1 }} flexWrap="wrap">
                {Object.entries(PRESETS).map(([key, preset]) => (
                    <Button
                        key={key}
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        disabled={disabled}
                        onClick={() => onChange(preset.build())}
                    >
                        {preset.label}
                    </Button>
                ))}
            </Stack>

            <Sheet variant="outlined" sx={{ p: 1, borderRadius: 'md', overflowX: 'auto' }}>
                <Box sx={{ minWidth: 560 }}>
                    <Stack direction="row" sx={{ pl: 4, mb: 0.3 }}>
                        {HOURS.map((h) => (
                            <Box key={h} sx={{ flex: 1, textAlign: 'center' }}>
                                {h % 3 === 0 && (
                                    <Typography level="body-xs" sx={{ fontSize: 9 }}>{h}</Typography>
                                )}
                            </Box>
                        ))}
                    </Stack>

                    {DAYS.map((dayLabel, day) => (
                        <Stack key={day} direction="row" alignItems="center" sx={{ mb: 0.2 }}>
                            <Typography
                                level="body-xs"
                                sx={{ width: 32, cursor: disabled ? 'default' : 'pointer', fontWeight: 600 }}
                                onClick={() => toggleDay(day)}
                            >
                                {dayLabel}
                            </Typography>
                            {HOURS.map((hour) => (
                                <Box
                                    key={hour}
                                    onClick={() => toggle(day, hour)}
                                    sx={{
                                        flex: 1, height: 16, mx: '1px', borderRadius: '2px',
                                        cursor: disabled ? 'default' : 'pointer',
                                        bgcolor: isOn(day, hour) ? 'primary.400' : 'neutral.softBg',
                                        transition: '.1s',
                                        '&:hover': disabled ? {} : { opacity: 0.75 },
                                    }}
                                />
                            ))}
                        </Stack>
                    ))}
                </Box>
            </Sheet>

            <Typography level="body-xs" color="neutral" sx={{ mt: 0.8 }}>
                {isAlways
                    ? 'Показы идут круглосуточно'
                    : 'Показы только в отмеченные часы. Клик по дню — включить или выключить сутки целиком'}
            </Typography>
        </Box>
    );
}